import type { PositionRow, StoredDocument } from '../storage/index.js';

/** D-4: free tier = 5-slot library. Caps are raisable, never lowerable. */
export const LIBRARY_MAX_SLOTS = 5;

/** True when another document may be imported. Delete-to-rotate always
 * works because the cap only gates ADDING (P1 brief, ratified copy). */
export function canAddDocument(documentCount: number): boolean {
  return documentCount < LIBRARY_MAX_SLOTS;
}

export type LibrarySort = 'recent' | 'progress' | 'title';

export interface LibraryEntry {
  readonly doc: StoredDocument;
  readonly position: PositionRow | undefined;
  /** 0–1 read fraction, from position over wordCount. */
  readonly progress: number;
}

export function toEntry(doc: StoredDocument, position: PositionRow | undefined): LibraryEntry {
  const progress =
    position === undefined || doc.wordCount === 0
      ? 0
      : Math.min(1, position.wordIdx / doc.wordCount);
  return { doc, position, progress };
}

export function sortEntries(entries: readonly LibraryEntry[], sort: LibrarySort): LibraryEntry[] {
  const copy = [...entries];
  switch (sort) {
    case 'recent':
      // Most recently read first; never-read docs by add time.
      return copy.sort(
        (a, b) =>
          (b.position?.updatedAt ?? b.doc.addedAt) - (a.position?.updatedAt ?? a.doc.addedAt),
      );
    case 'progress':
      return copy.sort((a, b) => b.progress - a.progress);
    case 'title':
      return copy.sort((a, b) => a.doc.title.localeCompare(b.doc.title));
  }
}

/** Estimated remaining minutes at the user's WPM (Library list column). */
export function minutesRemaining(entry: LibraryEntry, wpm: number): number {
  const wordsLeft = Math.max(0, entry.doc.wordCount - (entry.position?.wordIdx ?? 0));
  return Math.ceil(wordsLeft / Math.max(1, wpm));
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
